import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { StickyPageHeader } from "@/components/layout/PageHeader";
import Footer from "@/components/layout/Footer";
import { GlobalNavSidebar } from "@/components/layout/GlobalNavSidebar";
import { HomeBanners, PublicPageBackground } from "@/components/home";
import { useAuth } from "@/hooks/use-auth";
import { useOneSignal } from "@/contexts/OneSignalContext";
import { getPermissionStatus, isOneSignalAvailable } from "@lyder-talk/core";

interface PublicLayoutProps {
  showBanners?: boolean;
  showBackground?: boolean;
  showFooter?: boolean;
  fullHeight?: boolean;
  background?: React.ReactNode;
  mainClassName?: string;
}

export const PublicLayout = ({
  showBanners = true,
  showBackground = true,
  showFooter = true,
  fullHeight = false,
  background,
  mainClassName = "",
}: PublicLayoutProps) => {
  const { data: isAuthenticated } = useAuth();
  const location = useLocation();
  const isSharedSession = /^\/session\/[^/]+$/.test(location.pathname);
  const { error: oneSignalError, isRetrying, retry, dismiss } = useOneSignal();
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | null>(null);

  // Check notification permission status
  useEffect(() => {
    const checkNotificationPermission = () => {
      try {
        const status = getPermissionStatus();
        if (status.browser === "granted" && status.oneSignal) {
          setNotificationPermission("granted");
        } else if (status.browser === "denied") {
          setNotificationPermission("denied");
        } else {
          setNotificationPermission("default");
        }
      } catch (error) {
        console.error("Error checking notification permission:", error);
        if ("Notification" in window) {
          setNotificationPermission(Notification.permission);
        }
      }
    };

    checkNotificationPermission();

    // Poll for OneSignal availability until it's ready
    const interval = setInterval(() => {
      if (isOneSignalAvailable() && window.OneSignal?.User?.PushSubscription) {
        checkNotificationPermission();
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    <div
      className={`relative flex ${fullHeight ? "h-screen overflow-hidden" : "min-h-screen"} ${isSharedSession ? "bg-background" : "bg-gradient-to-br from-primary/[0.03] via-background to-secondary/[0.03] dark:from-primary/[0.06] dark:via-background dark:to-secondary/[0.06]"}`}
      style={{ marginRight: "var(--ai-sidebar-width, 0px)", transition: "margin-right 0.25s ease-out" }}
    >
      {/* Global left nav — desktop only; mobile keeps bottom nav + hamburger */}
      <GlobalNavSidebar />

      <div className="relative flex min-w-0 flex-1 flex-col">

        {showBackground && !isSharedSession && (background || <PublicPageBackground />)}

        {showBanners && (
          <HomeBanners
            isAuthenticated={!!isAuthenticated}
            oneSignalError={oneSignalError}
            isOneSignalRetrying={isRetrying}
            onRetryOneSignal={retry}
            onDismissOneSignal={dismiss}
            notificationPermission={notificationPermission}
          />
        )}

        {/* Top header — mobile only. Desktop uses the left sidebar for
            brand, nav, search entry, notifications, and account. */}
        <div className="md:hidden">
          <StickyPageHeader className="justify-end" />
        </div>

        <main className={`relative z-10 flex-1 ${isSharedSession ? "" : "container mx-auto"} px-2 sm:px-3 py-4 sm:py-6 pb-14 md:pb-6 ${mainClassName}`}>
          <Outlet />
        </main>

        {showFooter && !isSharedSession && <Footer />}
      </div>
    </div>
  );
};

export default PublicLayout;
